import React, { useEffect, useRef, useState } from 'react';
import {
  Bookmark,
  Check,
  Copy,
  Download,
  ExternalLink,
  EyeOff,
  Link2,
  Lock,
  LogOut,
  Plus,
  RotateCw,
  Shield,
  Sliders,
  Trash2,
  Unlock,
  Volume2,
  VolumeX,
  X,
} from 'lucide-react';
import {
  detectCsvColumns,
  FORMAT_PRESETS,
  STARTER_DECKS,
  suggestCountersForCard,
  type AuthProviderId,
  type ClientGameView,
  type CsvColumnMapping,
  type DeckExportFormat,
  type DeckImportResult,
  type FormatId,
  type GameIntent,
  type ImportedDeckPayload,
  type PublicCardView,
} from '#shared';
import { soundEngine, type SoundEventId, type SoundPreferences } from '../audio/soundEngine.ts';
import { getSessionBaseUrl, playtableApi, type AccountProfileDTO, type SavedDeckDTO } from '../lib/clientApi.ts';
import { CardVisual } from './CardVisual.tsx';

/**
 * Locks background document scrolling whenever any modal is mounted
 * ("Scrolling inside a popup never scrolls the page behind it").
 */
function ModalShell({
  title,
  onClose,
  children,
  maxWidth = 'max-w-lg',
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
  maxWidth?: string;
}) {
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      onClick={onClose}
      className="fixed inset-0 z-[100] bg-black/75 backdrop-blur-sm flex items-center justify-center p-3 overscroll-contain"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className={`w-full ${maxWidth} max-h-[88vh] rounded-2xl bg-slate-950 border border-slate-800 shadow-2xl flex flex-col overflow-hidden overscroll-contain`}
      >
        <div className="px-4 py-3 border-b border-slate-800 flex items-center justify-between shrink-0">
          <h2 className="font-bold text-sm text-slate-100">{title}</h2>
          <button
            type="button"
            aria-label="Close modal"
            onClick={onClose}
            className="p-1 rounded-lg hover:bg-slate-800 text-slate-400 hover:text-white"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-4 overflow-y-auto overscroll-contain space-y-4 text-xs text-slate-200">
          {children}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// 1. CARD INSPECTOR & CONTEXT ACTION MODAL
// ============================================================================

export function CardInspectorModal({
  card,
  canControl,
  battlefieldTargets,
  savedForLater,
  onDispatch,
  onClose,
}: {
  card: PublicCardView;
  canControl: boolean;
  battlefieldTargets: PublicCardView[];
  savedForLater?: string[] | null;
  onDispatch: (intent: GameIntent) => void;
  onClose: () => void;
}) {
  const [customCounter, setCustomCounter] = useState('charge');
  const holdTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const def = card.card;
  const scryfallUrl = def
    ? `https://scryfall.com/search?q=!"${encodeURIComponent(def.name)}"`
    : 'https://scryfall.com';

  const suggestedCounters = suggestCountersForCard(def?.oracleText ?? '', def?.typeLine ?? '');
  const isBookmarked = Boolean(
    def && savedForLater?.some((n) => n.toLowerCase() === def.name.toLowerCase())
  );

  const stopHold = () => {
    if (holdTimerRef.current) {
      clearInterval(holdTimerRef.current);
      holdTimerRef.current = null;
    }
  };

  useEffect(() => stopHold, []);

  const startHoldCounter = (counter: string, delta: number) => {
    stopHold();
    onDispatch({ type: 'ADJUST_CARD_COUNTER', instanceId: card.instanceId, counter, delta });
    holdTimerRef.current = setInterval(() => {
      onDispatch({ type: 'ADJUST_CARD_COUNTER', instanceId: card.instanceId, counter, delta });
    }, 160);
  };

  return (
    <ModalShell title={def ? def.name : 'Face-Down Card'} onClose={onClose} maxWidth="max-w-md">
      <div className="flex flex-col sm:flex-row gap-4 items-center sm:items-start">
        <CardVisual item={card} size="lg" />

        <div className="flex-1 space-y-2 w-full">
          {def ? (
            <>
              <div className="flex items-center justify-between gap-2">
                <span className="font-bold text-sm text-white">
                  {card.flipped && def.backName ? def.backName : def.name}
                </span>
                <span className="font-mono text-amber-300">{def.manaCost}</span>
              </div>
              <div className="text-slate-400 text-[11px]">
                {card.flipped && def.backTypeLine ? def.backTypeLine : def.typeLine}
              </div>
              <p className="whitespace-pre-line text-slate-200 bg-slate-900/90 border border-slate-800 rounded-lg p-2.5 leading-relaxed">
                {(card.flipped && def.backOracleText ? def.backOracleText : def.oracleText) ||
                  'Sandbox card object — move freely between zones.'}
              </p>
              <div className="flex flex-wrap items-center gap-2">
                <a
                  href={scryfallUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 text-cyan-400 hover:underline text-[11px]"
                >
                  <span>View Oracle & Rulings on Scryfall</span>
                  <ExternalLink className="w-3 h-3" />
                </a>
                {savedForLater !== undefined && (
                  <button
                    type="button"
                    onClick={() => onDispatch({ type: 'TOGGLE_SAVE_FOR_LATER', cardName: def.name })}
                    className={`px-2 py-0.5 rounded border text-[10px] font-bold inline-flex items-center gap-1 ${
                      isBookmarked
                        ? 'bg-amber-500/20 border-amber-400 text-amber-200'
                        : 'bg-slate-900 border-slate-700 text-slate-300 hover:text-white'
                    }`}
                  >
                    <Bookmark className="w-3 h-3" />
                    <span>{isBookmarked ? 'Saved in Tray' : 'Save for Later'}</span>
                  </button>
                )}
              </div>
            </>
          ) : (
            <p className="text-slate-400">
              This card is face down. Only its controller or authorized peekers can see its face.
            </p>
          )}
        </div>
      </div>

      {canControl && (
        <div className="space-y-3 pt-2 border-t border-slate-800">
          {/* Zone Movement */}
          <div>
            <div className="text-[10px] font-bold uppercase text-slate-400 mb-1.5">Move To Zone</div>
            <div className="flex flex-wrap gap-1.5">
              {(
                [
                  ['battlefield', 'Battlefield', false],
                  ['battlefield', 'Play Face-Down', true],
                  ['hand', 'Hand', false],
                  ['graveyard', 'Graveyard', false],
                  ['exile', 'Exile', false],
                  ['exile', 'Exile Face-Down', true],
                  ['command', 'Command Zone', false],
                  ['library', 'Top of Library', false],
                ] as const
              ).map(([zone, label, fd]) => (
                <button
                  key={`${zone}_${label}`}
                  type="button"
                  onClick={() => {
                    onDispatch({
                      type: 'MOVE_CARD',
                      instanceId: card.instanceId,
                      toZone: zone,
                      faceDown: fd,
                      position: 'top',
                    });
                    onClose();
                  }}
                  className="px-2.5 py-1 rounded-lg bg-slate-900 hover:bg-slate-800 border border-slate-700 font-semibold text-slate-200"
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* State & Counters */}
          <div className="flex flex-wrap gap-1.5 items-center">
            <button
              type="button"
              onClick={() => onDispatch({ type: 'TAP_CARD', instanceId: card.instanceId })}
              className="px-2.5 py-1 rounded-lg bg-slate-900 hover:bg-slate-800 border border-slate-700"
            >
              {card.tapped ? 'Untap' : 'Tap'}
            </button>
            {def?.backName && (
              <button
                type="button"
                onClick={() => onDispatch({ type: 'FLIP_CARD', instanceId: card.instanceId })}
                className="px-2.5 py-1 rounded-lg bg-slate-900 hover:bg-slate-800 border border-slate-700 flex items-center gap-1"
              >
                <RotateCw className="w-3 h-3" />
                <span>Transform / Flip</span>
              </button>
            )}
            <button
              type="button"
              onClick={() => onDispatch({ type: 'TOGGLE_FACE_DOWN', instanceId: card.instanceId })}
              className="px-2.5 py-1 rounded-lg bg-slate-900 hover:bg-slate-800 border border-slate-700"
            >
              {card.faceDown ? 'Turn Face Up' : 'Turn Face Down'}
            </button>
            {def && (
              <button
                type="button"
                onClick={() => {
                  onDispatch({
                    type: 'CREATE_TOKEN',
                    token: { name: def.name, typeLine: def.typeLine, colors: def.colors },
                    count: 1,
                    copyOfInstanceId: card.instanceId,
                  });
                  onClose();
                }}
                className="px-2.5 py-1 rounded-lg bg-emerald-950/80 hover:bg-emerald-900 border border-emerald-600/60 text-emerald-200"
              >
                Copy as Token
              </button>
            )}
          </div>

          {/* Counters Quick Stepper (Auto-Suggested + Press-and-Hold Repeat) */}
          <div className="space-y-1.5">
            <div className="text-[10px] font-bold uppercase text-slate-400">
              Permanent Counters (Hold +/− to repeat)
            </div>
            <div className="flex flex-wrap gap-1.5">
              {suggestedCounters.map((ctr) => (
                <div key={ctr} className="flex items-center rounded-lg bg-slate-900 border border-slate-700 overflow-hidden">
                  <button
                    type="button"
                    onPointerDown={() => startHoldCounter(ctr, -1)}
                    onPointerUp={stopHold}
                    onPointerLeave={stopHold}
                    className="px-2 py-1 hover:bg-slate-800 text-rose-300 font-bold select-none"
                  >
                    −
                  </button>
                  <span className="px-2 text-[10px] font-semibold">
                    {ctr} ({card.counters[ctr] ?? 0})
                  </span>
                  <button
                    type="button"
                    onPointerDown={() => startHoldCounter(ctr, 1)}
                    onPointerUp={stopHold}
                    onPointerLeave={stopHold}
                    className="px-2 py-1 hover:bg-slate-800 text-emerald-300 font-bold select-none"
                  >
                    +
                  </button>
                </div>
              ))}
            </div>
            <div className="flex items-center gap-1.5 pt-1">
              <input
                value={customCounter}
                onChange={(e) => setCustomCounter(e.target.value)}
                placeholder="Custom counter"
                className="px-2 py-1 rounded bg-slate-900 border border-slate-700 text-xs w-32"
              />
              <button
                type="button"
                onClick={() =>
                  customCounter.trim() &&
                  onDispatch({
                    type: 'ADJUST_CARD_COUNTER',
                    instanceId: card.instanceId,
                    counter: customCounter.trim(),
                    delta: 1,
                  })
                }
                className="px-2.5 py-1 rounded bg-indigo-600 hover:bg-indigo-500 text-white font-semibold"
              >
                +1 Custom
              </button>
            </div>
          </div>

          {/* Attach / Equip */}
          {card.zone === 'battlefield' && battlefieldTargets.length > 1 && (
            <div>
              <div className="text-[10px] font-bold uppercase text-slate-400 mb-1">Attach / Equip To</div>
              <div className="flex flex-wrap gap-1">
                {card.attachedToId && (
                  <button
                    type="button"
                    onClick={() => onDispatch({ type: 'ATTACH_CARD', instanceId: card.instanceId, targetInstanceId: null })}
                    className="px-2 py-1 rounded bg-rose-950 border border-rose-600/60 text-rose-200"
                  >
                    Detach
                  </button>
                )}
                {battlefieldTargets
                  .filter((t) => t.instanceId !== card.instanceId)
                  .slice(0, 6)
                  .map((t) => (
                    <button
                      key={t.instanceId}
                      type="button"
                      onClick={() =>
                        onDispatch({ type: 'ATTACH_CARD', instanceId: card.instanceId, targetInstanceId: t.instanceId })
                      }
                      className="px-2 py-1 rounded bg-slate-900 hover:bg-slate-800 border border-slate-700 flex items-center gap-1"
                    >
                      <Link2 className="w-3 h-3 text-sky-400" />
                      <span>{t.card?.name ?? 'Face-down'}</span>
                    </button>
                  ))}
              </div>
            </div>
          )}
        </div>
      )}
    </ModalShell>
  );
}

// ============================================================================
// 2. PRIVATE SCRY / LOOK TOP / LIBRARY SEARCH MODAL
// ============================================================================

export function PrivateLookModal({
  kind,
  cards,
  onDispatch,
}: {
  kind: 'scry' | 'look_top' | 'search_library';
  cards: PublicCardView[];
  onDispatch: (intent: GameIntent) => void;
}) {
  const [bottomSet, setBottomSet] = useState<Set<string>>(new Set());
  const [filter, setFilter] = useState('');

  const filtered = cards.filter(
    (c) => !filter.trim() || c.card?.name.toLowerCase().includes(filter.trim().toLowerCase())
  );

  return (
    <ModalShell
      title={
        kind === 'scry'
          ? `Scry ${cards.length} (Private View)`
          : kind === 'search_library'
          ? `Search Library (${cards.length} cards — Private)`
          : `Looking at Top ${cards.length} Cards`
      }
      onClose={() => onDispatch({ type: 'CLOSE_PRIVATE_LOOK', shuffleAfter: kind === 'search_library' })}
      maxWidth="max-w-2xl"
    >
      {kind === 'search_library' && (
        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Search card name in library..."
          className="w-full px-3 py-2 rounded-lg bg-slate-900 border border-slate-700 text-xs"
        />
      )}

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 max-h-[52vh] overflow-y-auto p-1">
        {filtered.map((c) => {
          const isBottom = bottomSet.has(c.instanceId);
          return (
            <div key={c.instanceId} className="flex flex-col items-center gap-1.5 p-2 rounded-xl bg-slate-900/80 border border-slate-800">
              <CardVisual item={c} size="sm" />
              <div className="text-[11px] font-semibold text-center line-clamp-1">{c.card?.name}</div>

              {kind === 'scry' ? (
                <button
                  type="button"
                  onClick={() => {
                    const next = new Set(bottomSet);
                    if (next.has(c.instanceId)) next.delete(c.instanceId);
                    else next.add(c.instanceId);
                    setBottomSet(next);
                  }}
                  className={`w-full py-1 rounded font-bold text-[10px] ${
                    isBottom
                      ? 'bg-rose-600 text-white'
                      : 'bg-emerald-600/30 border border-emerald-500/60 text-emerald-200'
                  }`}
                >
                  {isBottom ? 'Bottom of Library' : 'Keep on Top'}
                </button>
              ) : (
                <div className="flex flex-wrap gap-1 justify-center w-full">
                  <button
                    type="button"
                    onClick={() =>
                      onDispatch({
                        type: 'SEARCH_LIBRARY_PICK',
                        instanceId: c.instanceId,
                        destination: 'hand',
                        shuffleAfter: kind === 'search_library',
                      })
                    }
                    className="px-2 py-0.5 rounded bg-amber-500 text-slate-950 font-bold text-[10px]"
                  >
                    → Hand
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      onDispatch({
                        type: 'SEARCH_LIBRARY_PICK',
                        instanceId: c.instanceId,
                        destination: 'battlefield',
                        shuffleAfter: kind === 'search_library',
                      })
                    }
                    className="px-2 py-0.5 rounded bg-cyan-500 text-slate-950 font-bold text-[10px]"
                  >
                    → Play
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      onDispatch({
                        type: 'SEARCH_LIBRARY_PICK',
                        instanceId: c.instanceId,
                        destination: 'top',
                        shuffleAfter: kind === 'search_library',
                      })
                    }
                    className="px-2 py-0.5 rounded bg-slate-800 text-slate-200 text-[10px]"
                  >
                    → Top
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="flex justify-end gap-2 pt-2 border-t border-slate-800">
        {kind === 'scry' ? (
          <button
            type="button"
            onClick={() => {
              const topIds = cards.filter((c) => !bottomSet.has(c.instanceId)).map((c) => c.instanceId);
              const bottomIds = cards.filter((c) => bottomSet.has(c.instanceId)).map((c) => c.instanceId);
              onDispatch({ type: 'SCRY_RESOLVE', topIds, bottomIds });
            }}
            className="px-4 py-1.5 rounded-lg bg-amber-500 hover:bg-amber-400 text-slate-950 font-extrabold"
          >
            Confirm Scry
          </button>
        ) : (
          <button
            type="button"
            onClick={() =>
              onDispatch({ type: 'CLOSE_PRIVATE_LOOK', shuffleAfter: kind === 'search_library' })
            }
            className="px-4 py-1.5 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-extrabold"
          >
            {kind === 'search_library' ? 'Close & Shuffle' : 'Done'}
          </button>
        )}
      </div>
    </ModalShell>
  );
}

// ============================================================================
// 3. ZONE BROWSER MODAL (GRAVEYARD / EXILE / SIDEBOARD)
// ============================================================================

export function ZoneBrowserModal({
  title,
  zoneKind = 'graveyard',
  cards,
  canMove,
  onDispatch,
  onInspect,
  onClose,
}: {
  title: string;
  zoneKind?: 'graveyard' | 'exile' | 'sideboard';
  cards: PublicCardView[];
  canMove: boolean;
  onDispatch: (intent: GameIntent) => void;
  onInspect: (card: PublicCardView) => void;
  onClose: () => void;
}) {
  const [query, setQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState<'all' | 'Creature' | 'Land' | 'Artifact' | 'Enchantment' | 'Instant' | 'Sorcery' | 'Planeswalker'>('all');
  const [sortMode, setSortMode] = useState<'pile' | 'alpha' | 'cmc'>('pile');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [lastClickedIdx, setLastClickedIdx] = useState<number | null>(null);

  const filtered = cards
    .filter((c) => {
      if (typeFilter !== 'all' && !(c.card?.typeLine ?? '').includes(typeFilter)) return false;
      if (!query.trim()) return true;
      const q = query.trim().toLowerCase();
      return (
        (c.card?.name ?? 'face-down').toLowerCase().includes(q) ||
        (c.card?.typeLine ?? '').toLowerCase().includes(q)
      );
    })
    .slice()
    .sort((a, b) => {
      if (sortMode === 'alpha') return (a.card?.name ?? '').localeCompare(b.card?.name ?? '');
      if (sortMode === 'cmc') return (a.card?.cmc ?? 0) - (b.card?.cmc ?? 0);
      return 0; // pile order (top of pile first)
    });

  const previewCard =
    cards.find((c) => c.instanceId === selectedIds[selectedIds.length - 1]) ?? null;

  const toggleSelect = (id: string, idx: number, shiftKey: boolean) => {
    if (shiftKey && lastClickedIdx !== null) {
      const lo = Math.min(lastClickedIdx, idx);
      const hi = Math.max(lastClickedIdx, idx);
      const rangeIds = filtered.slice(lo, hi + 1).map((c) => c.instanceId);
      setSelectedIds((prev) => Array.from(new Set([...prev, ...rangeIds])));
    } else {
      setSelectedIds((prev) =>
        prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
      );
    }
    setLastClickedIdx(idx);
  };

  const moveSelected = (toZone: 'hand' | 'battlefield' | 'graveyard' | 'exile' | 'library', position?: 'top' | 'bottom') => {
    for (const instanceId of selectedIds) {
      onDispatch({ type: 'MOVE_CARD', instanceId, toZone, position });
    }
    setSelectedIds([]);
  };

  return (
    <ModalShell title={`${title} (${cards.length})`} onClose={onClose} maxWidth="max-w-3xl">
      <div className="grid grid-cols-1 md:grid-cols-[1.35fr_0.9fr] gap-4">
        {/* Left Column: Search, Filters, Multi-Select Pile List */}
        <div className="space-y-2.5">
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by name or type..."
            className="w-full px-3 py-1.5 rounded-xl bg-slate-900 border border-slate-700 text-xs"
          />

          <div className="grid grid-cols-2 gap-2">
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value as any)}
              className="px-2.5 py-1.5 rounded-xl bg-slate-900 border border-slate-700 text-xs"
            >
              <option value="all">All types</option>
              <option value="Creature">Creatures</option>
              <option value="Land">Lands</option>
              <option value="Artifact">Artifacts</option>
              <option value="Enchantment">Enchantments</option>
              <option value="Instant">Instants</option>
              <option value="Sorcery">Sorceries</option>
              <option value="Planeswalker">Planeswalkers</option>
            </select>
            <select
              value={sortMode}
              onChange={(e) => setSortMode(e.target.value as any)}
              className="px-2.5 py-1.5 rounded-xl bg-slate-900 border border-slate-700 text-xs"
            >
              <option value="pile">Pile order</option>
              <option value="alpha">Alphabetical</option>
              <option value="cmc">Mana Value (CMC)</option>
            </select>
          </div>

          <div className="flex items-center justify-between text-[11px] text-slate-400 pt-1 border-t border-slate-800/80">
            <span>
              {filtered.length} of {cards.length} cards
            </span>
            <div className="flex items-center gap-2.5">
              {filtered.length > 0 && (
                <button
                  type="button"
                  onClick={() => {
                    const rand = filtered[Math.floor(Math.random() * filtered.length)];
                    if (rand) setSelectedIds([rand.instanceId]);
                  }}
                  className="text-emerald-400 hover:underline font-semibold"
                >
                  Choose at random
                </button>
              )}
              <button
                type="button"
                onClick={() => setSelectedIds(filtered.map((c) => c.instanceId))}
                className="text-emerald-400 hover:underline font-semibold"
              >
                Select all shown
              </button>
              <button
                type="button"
                onClick={() => setSelectedIds([])}
                className="text-slate-500 hover:text-slate-300"
              >
                Clear
              </button>
            </div>
          </div>

          <div className="max-h-72 overflow-y-auto space-y-1.5 pr-1">
            {filtered.length === 0 ? (
              <div className="py-10 text-center text-slate-500">No matching cards in zone.</div>
            ) : (
              filtered.map((c, idx) => {
                const checked = selectedIds.includes(c.instanceId);
                return (
                  <div
                    key={c.instanceId}
                    onClick={(e) => toggleSelect(c.instanceId, idx, e.shiftKey)}
                    className={`p-2 rounded-xl border flex items-center justify-between gap-2.5 cursor-pointer transition-colors ${
                      checked
                        ? 'bg-indigo-950/60 border-indigo-400'
                        : 'bg-slate-900/90 border-slate-800 hover:border-slate-700'
                    }`}
                  >
                    <div className="flex items-center gap-2.5 min-w-0">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => {}}
                        className="accent-indigo-500 pointer-events-none"
                      />
                      <CardVisual item={c} size="xs" />
                      <div className="min-w-0">
                        <div className="font-bold text-slate-100 truncate">
                          {c.card?.name ?? 'Face-down card'}
                        </div>
                        <div className="text-[10px] text-slate-400 truncate">
                          {c.card?.typeLine ?? 'Hidden face'}
                        </div>
                        {idx === 0 && sortMode === 'pile' && (
                          <span className="text-[10px] font-semibold text-emerald-400">Top of pile</span>
                        )}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onInspect(c);
                      }}
                      className="px-2 py-1 rounded bg-slate-800 hover:bg-slate-700 text-[10px] text-slate-300 shrink-0"
                    >
                      Inspect
                    </button>
                  </div>
                );
              })
            )}
          </div>
          <div className="text-[10px] text-slate-500">Tip: Shift-click to select a range of cards.</div>
        </div>

        {/* Right Column: Selected Preview & Batch Move Actions */}
        <div className="rounded-xl bg-slate-900/60 border border-slate-800 p-3 flex flex-col justify-between gap-3">
          <div className="flex flex-col items-center justify-center min-h-[170px] rounded-xl border border-dashed border-slate-800 p-3">
            {previewCard ? (
              <CardVisual item={previewCard} size="md" onClick={() => onInspect(previewCard)} />
            ) : (
              <span className="text-slate-500 text-xs">Select a card to preview it here.</span>
            )}
          </div>

          <div className="space-y-2">
            <div className="text-[10px] font-extrabold uppercase tracking-wider text-emerald-400">
              {selectedIds.length === 0 ? 'No cards selected' : `${selectedIds.length} card(s) selected`}
            </div>
            {canMove && (
              <>
                <div className="text-[11px] font-bold text-slate-300">Move selected to</div>
                <div className="grid grid-cols-2 gap-1.5">
                  <button
                    type="button"
                    disabled={selectedIds.length === 0}
                    onClick={() => moveSelected('hand')}
                    className="px-2.5 py-1.5 rounded-lg bg-slate-900 hover:bg-slate-800 disabled:opacity-40 border border-slate-700 font-semibold"
                  >
                    To hand
                  </button>
                  <button
                    type="button"
                    disabled={selectedIds.length === 0}
                    onClick={() => moveSelected('battlefield')}
                    className="px-2.5 py-1.5 rounded-lg bg-slate-900 hover:bg-slate-800 disabled:opacity-40 border border-slate-700 font-semibold"
                  >
                    To battlefield
                  </button>
                  {zoneKind !== 'exile' ? (
                    <button
                      type="button"
                      disabled={selectedIds.length === 0}
                      onClick={() => moveSelected('exile')}
                      className="px-2.5 py-1.5 rounded-lg bg-slate-900 hover:bg-slate-800 disabled:opacity-40 border border-slate-700 font-semibold"
                    >
                      To exile
                    </button>
                  ) : (
                    <button
                      type="button"
                      disabled={selectedIds.length === 0}
                      onClick={() => moveSelected('graveyard')}
                      className="px-2.5 py-1.5 rounded-lg bg-slate-900 hover:bg-slate-800 disabled:opacity-40 border border-slate-700 font-semibold"
                    >
                      To graveyard
                    </button>
                  )}
                  <button
                    type="button"
                    disabled={selectedIds.length === 0}
                    onClick={() => moveSelected('library', 'top')}
                    className="px-2.5 py-1.5 rounded-lg bg-slate-900 hover:bg-slate-800 disabled:opacity-40 border border-slate-700 font-semibold"
                  >
                    Library top
                  </button>
                  <button
                    type="button"
                    disabled={selectedIds.length === 0}
                    onClick={() => moveSelected('library', 'bottom')}
                    className="px-2.5 py-1.5 rounded-lg bg-slate-900 hover:bg-slate-800 disabled:opacity-40 border border-slate-700 font-semibold col-span-2"
                  >
                    Library bottom
                  </button>
                </div>
              </>
            )}
          </div>

          <div className="flex justify-end pt-2 border-t border-slate-800">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-1.5 rounded-xl bg-blue-500 hover:bg-blue-400 text-slate-950 font-extrabold"
            >
              Done
            </button>
          </div>
        </div>
      </div>
    </ModalShell>
  );
}

// ============================================================================
// 4. DECK MANAGER & UNIVERSAL IMPORTER MODAL
// ============================================================================

export function DeckLoaderModal({
  format,
  mySeat,
  onDispatch,
  onOpenCosmetics,
  onBuyDeck,
  onClose,
}: {
  format: FormatId;
  mySeat?: ClientGameView['seats'][string] | null;
  onDispatch: (intent: GameIntent) => void;
  onOpenCosmetics?: () => void;
  onBuyDeck?: (deck: ImportedDeckPayload) => void;
  onClose: () => void;
}) {
  const [activeTab, setActiveTab] = useState<'library' | 'import' | 'files'>('library');
  const [savedDecks, setSavedDecks] = useState<SavedDeckDTO[]>([]);
  const [previousDeck, setPreviousDeck] = useState<ImportedDeckPayload | null>(() => {
    try {
      const raw = localStorage.getItem('pt_previous_deck');
      return raw ? (JSON.parse(raw) as ImportedDeckPayload) : null;
    } catch {
      return null;
    }
  });
  const [deckName, setDeckName] = useState('Custom Brew');
  const [urlInput, setUrlInput] = useState('');
  const [rawText, setRawText] = useState(
    `Commander\n1 Atraxa, Praetors' Voice\n\nDeck\n1 Sol Ring\n1 Arcane Signet\n1 Rhystic Study\n1 Smothering Tithe\n4 Swords to Plowshares\n4 Counterspell\n4 Delver of Secrets // Insectile Aberration\n12 Island\n12 Plains\n\nSideboard\n1 Mystic Remora`
  );
  const [showTransparency, setShowTransparency] = useState(true);
  const [saveToLibrary, setSaveToLibrary] = useState(true);
  const [isOcrScan, setIsOcrScan] = useState(false);
  const [isCubeInput, setIsCubeInput] = useState(false);
  const [cubeMode, setCubeMode] = useState<'sealed' | 'battlebox'>('sealed');
  const [csvHeaders, setCsvHeaders] = useState<string[] | null>(null);
  const [csvMapping, setCsvMapping] = useState<CsvColumnMapping | undefined>(undefined);
  const [importReview, setImportReview] = useState<DeckImportResult | null>(null);
  const [copiedSlug, setCopiedSlug] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const legalArtPool = mySeat?.deckMeta?.legalCosmeticArt ?? [];

  const refreshSaved = async () => {
    try {
      const res = await playtableApi.listSavedDecks();
      setSavedDecks(res.decks);
    } catch {
      // Guest or not signed in
    }
  };

  useEffect(() => {
    refreshSaved();
  }, []);

  const commitDeckToSeat = async (deck: ImportedDeckPayload, isCube = false) => {
    let finalDeck = deck;
    if (saveToLibrary && !isCube) {
      try {
        const saved = await playtableApi.saveDeck({
          deckId: deck.deckId,
          deck,
          cosmetics: {
            sleeveCardName: mySeat?.sleeveCardName ?? null,
            playmatCardName: mySeat?.playmatCardName ?? null,
            focalPoint: mySeat?.playmatFocalPoint ?? 'center',
          },
        });
        finalDeck = saved.savedDeck.deck;
      } catch {
        // Ignore if not signed in
      }
    }
    try {
      localStorage.setItem('pt_previous_deck', JSON.stringify(finalDeck));
      setPreviousDeck(finalDeck);
    } catch {
      // ignore storage quota
    }
    if (isCube) {
      onDispatch({ type: 'LOAD_CUBE_POOL', cube: finalDeck, mode: cubeMode, cardsPerSeat: 45 });
    } else {
      onDispatch({ type: 'LOAD_DECK', deck: finalDeck, showTransparency });
    }
  };

  const handleUniversalImport = async (inputStr: string, filename?: string) => {
    setBusy(true);
    try {
      const res = await playtableApi.importDeckUniversal({
        rawInput: inputStr,
        filename,
        deckName,
        format,
        csvMapping,
        forceSource: isOcrScan ? 'ocr' : isCubeInput ? 'cube' : undefined,
      });
      setImportReview(res);
      if (
        !res.requiresPasteFallback &&
        res.unresolvedLines.length === 0 &&
        res.warnings.length === 0
      ) {
        await commitDeckToSeat(res.deck, Boolean(res.isCube));
        onClose();
      }
    } finally {
      setBusy(false);
    }
  };

  const handleFileUpload = async (file: File) => {
    const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
    setDeckName(file.name.replace(/\.[^.]+$/, ''));

    if (ext === 'zip') {
      setBusy(true);
      try {
        const buf = await file.arrayBuffer();
        const bytes = new Uint8Array(buf);
        let binary = '';
        for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
        const zipBase64 = btoa(binary);
        const batch = await playtableApi.importDeckZip(zipBase64, format, true);
        await refreshSaved();
        if (batch.decks[0]) {
          await commitDeckToSeat(batch.decks[0].deck);
        }
        setActiveTab('library');
      } finally {
        setBusy(false);
      }
      return;
    }

    const text = await file.text();
    setRawText(text);
    if (ext === 'csv') {
      const detected = detectCsvColumns(text);
      setCsvHeaders(detected.headers);
      setCsvMapping(detected.suggestedMapping);
    } else {
      setCsvHeaders(null);
      setCsvMapping(undefined);
    }
    await handleUniversalImport(text, file.name);
  };

  const handleDownloadExport = async (deck: ImportedDeckPayload, exportFormat: DeckExportFormat) => {
    const res = await playtableApi.exportDeck(deck, exportFormat);
    const extMap: Record<DeckExportFormat, string> = {
      plain: 'txt',
      arena: 'txt',
      mtgo_txt: 'txt',
      mtgo_dek: 'dek',
      cockatrice_cod: 'cod',
      xmage_dck: 'dck',
      csv: 'csv',
      json: 'json',
    };
    const blob = new Blob([res.text], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${deck.name.replace(/[^a-z0-9_-]+/gi, '_')}.${extMap[exportFormat]}`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <ModalShell title="Deck Manager — Select, Import, Share & Customize" onClose={onClose} maxWidth="max-w-2xl">
      {/* Previous Deck 1-Tap Banner */}
      {previousDeck && (
        <div className="p-3 rounded-xl bg-indigo-950/50 border border-indigo-500/40 flex items-center justify-between gap-3">
          <div>
            <div className="text-[10px] font-bold uppercase text-indigo-300">Play your previous deck?</div>
            <div className="font-extrabold text-sm text-white">
              {previousDeck.commanders[0]?.name ?? previousDeck.name}
            </div>
          </div>
          <button
            type="button"
            onClick={() => {
              onDispatch({ type: 'LOAD_DECK', deck: previousDeck, showTransparency });
              onClose();
            }}
            className="px-3.5 py-1.5 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white font-bold text-xs shrink-0"
          >
            Use previous
          </button>
        </div>
      )}

      {/* Navigation Tabs */}
      <div className="grid grid-cols-3 gap-1.5 p-1 rounded-xl bg-slate-900 border border-slate-800">
        {(
          [
            ['library', `My Decks & Precons (${savedDecks.length})`],
            ['import', 'Paste / URL Import'],
            ['files', 'File / CSV / .ZIP / Cube'],
          ] as const
        ).map(([id, label]) => (
          <button
            key={id}
            type="button"
            onClick={() => setActiveTab(id)}
            className={`py-1.5 px-2 rounded-lg font-bold text-[11px] transition-colors ${
              activeTab === id ? 'bg-violet-600 text-white' : 'text-slate-400 hover:text-white'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {activeTab === 'library' && (
        <div className="space-y-3">
          <div className="space-y-1.5">
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
              Try a Precon (Ready to Play)
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              {STARTER_DECKS.map((sd) => (
                <div
                  key={sd.deckId}
                  className="p-2.5 rounded-xl bg-slate-900 border border-slate-700 text-left space-y-2 flex flex-col justify-between"
                >
                  <div>
                    <div className="font-bold text-slate-100">{sd.name}</div>
                    <div className="text-[10px] text-slate-400">
                      {sd.commanders[0]?.name ?? '60-Card'} · Ready
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <button
                      type="button"
                      onClick={() => {
                        commitDeckToSeat(sd);
                        onClose();
                      }}
                      className="flex-1 py-1 px-2 rounded-lg bg-violet-600 hover:bg-violet-500 text-white font-bold text-[10px]"
                    >
                      Play Precon
                    </button>
                    {onBuyDeck && (
                      <button
                        type="button"
                        onClick={() => onBuyDeck(sd)}
                        className="py-1 px-2 rounded-lg bg-emerald-500/20 hover:bg-emerald-500/35 border border-emerald-400/50 text-emerald-200 font-bold text-[10px]"
                      >
                        Buy Deck
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-1.5 pt-2 border-t border-slate-800">
            <div className="flex items-center justify-between text-[10px] font-bold uppercase tracking-wider text-slate-400">
              <span>My Saved Deck Library</span>
              <span>{savedDecks.length} saved</span>
            </div>

            {savedDecks.length === 0 ? (
              <div className="py-5 text-center text-slate-500 rounded-xl border border-dashed border-slate-800">
                No saved decks yet — import from Deckstr, Moxfield, Archidekt, Arena, CSV, or .zip!
              </div>
            ) : (
              <div className="space-y-1.5 max-h-56 overflow-y-auto pr-1">
                {savedDecks.map((sd) => {
                  const shareUrl = `${getSessionBaseUrl()}?shareDeck=${encodeURIComponent(sd.shareSlug)}`;
                  return (
                    <div
                      key={sd.deckId}
                      className="p-2.5 rounded-xl bg-slate-900/90 border border-slate-800 flex flex-wrap items-center justify-between gap-2"
                    >
                      <div className="min-w-0">
                        <div className="font-bold text-slate-100 truncate">{sd.name}</div>
                        <div className="text-[10px] text-slate-400">
                          {sd.deck.commanders.map((c) => c.name).join(' + ') || sd.format.toUpperCase()} · Era{' '}
                          {sd.eraNumber} · {sd.trackedGames}/10 tracked
                        </div>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <button
                          type="button"
                          onClick={() => {
                            onDispatch({
                              type: 'LOAD_DECK',
                              deck: sd.deck,
                              showTransparency,
                              cosmetics: sd.cosmetics,
                            });
                            onClose();
                          }}
                          className="px-2.5 py-1 rounded-lg bg-amber-500 hover:bg-amber-400 text-slate-950 font-extrabold text-[11px]"
                        >
                          Load
                        </button>
                        {onBuyDeck && (
                          <button
                            type="button"
                            onClick={() => onBuyDeck(sd.deck)}
                            className="px-2 py-1 rounded-lg bg-emerald-500/20 hover:bg-emerald-500/35 border border-emerald-400/50 text-emerald-200 font-bold text-[10px]"
                          >
                            Buy Deck
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => {
                            navigator.clipboard?.writeText(shareUrl);
                            setCopiedSlug(sd.shareSlug);
                            setTimeout(() => setCopiedSlug(null), 1400);
                          }}
                          className="px-2 py-1 rounded-lg bg-slate-800 hover:bg-slate-700 text-[10px]"
                        >
                          {copiedSlug === sd.shareSlug ? 'Copied!' : 'Share'}
                        </button>
                        <select
                          defaultValue=""
                          aria-label={`Export ${sd.name}`}
                          onChange={(e) => {
                            if (e.target.value) {
                              handleDownloadExport(sd.deck, e.target.value as DeckExportFormat);
                              e.target.value = '';
                            }
                          }}
                          className="px-1.5 py-1 rounded-lg bg-slate-800 border border-slate-700 text-[10px]"
                        >
                          <option value="">Export...</option>
                          <option value="arena">MTG Arena</option>
                          <option value="plain">Plain Text</option>
                          <option value="mtgo_dek">MTGO .dek</option>
                          <option value="cockatrice_cod">Cockatrice .cod</option>
                          <option value="xmage_dck">XMage .dck</option>
                          <option value="csv">CSV</option>
                          <option value="json">JSON</option>
                        </select>
                        <button
                          type="button"
                          onClick={async () => {
                            await playtableApi.deleteSavedDeck(sd.deckId);
                            refreshSaved();
                          }}
                          className="p-1 rounded hover:bg-rose-950 text-rose-400"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Quick Import from URL Bar at Bottom of Deck Manager */}
          <div className="pt-2 border-t border-slate-800 space-y-1.5">
            <div className="text-[10px] font-bold uppercase text-slate-400">Import from URL</div>
            <div className="flex gap-2">
              <input
                value={urlInput}
                onChange={(e) => setUrlInput(e.target.value)}
                placeholder="Paste Deckstr, Moxfield, Archidekt, MTGGoldfish, Scryfall, Cube Cobra URL..."
                className="flex-1 px-3 py-1.5 rounded-xl bg-slate-900 border border-slate-700 text-xs"
              />
              <button
                type="button"
                disabled={busy || !urlInput.trim()}
                onClick={() => {
                  setActiveTab('import');
                  handleUniversalImport(urlInput.trim());
                }}
                className="px-3.5 py-1.5 rounded-xl bg-blue-600 hover:bg-blue-500 disabled:opacity-40 text-white font-extrabold flex items-center gap-1"
              >
                <Plus className="w-3.5 h-3.5" />
                <span>Add</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'import' && (
        <div className="space-y-2.5">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <input
              value={deckName}
              onChange={(e) => setDeckName(e.target.value)}
              placeholder="Deck Name"
              className="flex-1 px-3 py-1.5 rounded-lg bg-slate-900 border border-slate-700"
            />
            <label className="flex items-center gap-1.5 text-[11px] text-slate-300 cursor-pointer">
              <input
                type="checkbox"
                checked={saveToLibrary}
                onChange={(e) => setSaveToLibrary(e.target.checked)}
              />
              <span>Save to My Decks</span>
            </label>
            <label className="flex items-center gap-1.5 text-[11px] text-slate-300 cursor-pointer">
              <input
                type="checkbox"
                checked={showTransparency}
                onChange={(e) => setShowTransparency(e.target.checked)}
              />
              <span>Share Game Changers</span>
            </label>
          </div>

          <textarea
            rows={7}
            value={rawText}
            onChange={(e) => setRawText(e.target.value)}
            placeholder="Paste Arena, MTGO, Moxfield, Untap.in, or OCR card lines..."
            className="w-full px-3 py-2 rounded-lg bg-slate-900 border border-slate-700 font-mono text-[11px]"
          />

          <div className="flex flex-wrap items-center justify-between gap-2 text-[11px]">
            <label className="flex items-center gap-1.5 text-slate-400 cursor-pointer">
              <input
                type="checkbox"
                checked={isOcrScan}
                onChange={(e) => setIsOcrScan(e.target.checked)}
              />
              <span>Clean noisy Photo / Screenshot OCR text</span>
            </label>

            <button
              type="button"
              disabled={busy}
              onClick={() => handleUniversalImport(rawText)}
              className="px-4 py-1.5 rounded-xl bg-amber-500 hover:bg-amber-400 text-slate-950 font-extrabold"
            >
              {busy ? 'Importing...' : 'Validate & Load Deck'}
            </button>
          </div>
        </div>
      )}

      {activeTab === 'files' && (
        <div className="space-y-3">
          <div className="p-4 rounded-xl border border-dashed border-slate-700 bg-slate-900/60 text-center space-y-2">
            <div className="font-bold text-slate-200">
              Upload Deck File (.txt, .dec, .dek, .cod, .dck, .csv, .json) or Multi-Deck .zip
            </div>
            <div className="text-[11px] text-slate-400">
              Supports MTGO, Cockatrice, XMage/Forge, ManaBox, Delver Lens, Dragon Shield, TCGplayer, Cardsphere, and Cube Cobra.
            </div>
            <input
              type="file"
              accept=".txt,.dec,.dek,.cod,.dck,.csv,.json,.zip"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) handleFileUpload(f);
              }}
              className="mx-auto block text-xs text-slate-300"
            />
          </div>

          {/* Interactive CSV Column Mapper when CSV headers are detected */}
          {csvHeaders && csvMapping && (
            <div className="p-3 rounded-xl bg-slate-900 border border-slate-700 space-y-2">
              <div className="text-[10px] font-bold uppercase text-cyan-300">
                CSV Column Mapping ({csvHeaders.length} columns detected)
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-[11px]">
                {(
                  [
                    ['nameCol', 'Card Name'],
                    ['quantityCol', 'Quantity'],
                    ['setCol', 'Set Code'],
                    ['collectorNumberCol', 'Collector #'],
                    ['foilCol', 'Foil'],
                    ['boardCol', 'Board / Zone'],
                  ] as const
                ).map(([field, label]) => (
                  <label key={field} className="space-y-0.5">
                    <span className="text-slate-400">{label}</span>
                    <select
                      value={csvMapping[field] ?? ''}
                      onChange={(e) =>
                        setCsvMapping({ ...csvMapping, [field]: e.target.value || undefined })
                      }
                      className="w-full px-2 py-1 rounded bg-slate-950 border border-slate-700"
                    >
                      <option value="">(None)</option>
                      {csvHeaders.map((h) => (
                        <option key={h} value={h}>
                          {h}
                        </option>
                      ))}
                    </select>
                  </label>
                ))}
              </div>
              <button
                type="button"
                onClick={() => handleUniversalImport(rawText, `${deckName}.csv`)}
                className="px-3 py-1 rounded-lg bg-cyan-500 text-slate-950 font-bold"
              >
                Re-Parse CSV With Mapping
              </button>
            </div>
          )}

          {/* Cube Pool Controls */}
          <div className="p-3 rounded-xl bg-slate-900/80 border border-slate-800 flex flex-wrap items-center justify-between gap-2">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={isCubeInput}
                onChange={(e) => setIsCubeInput(e.target.checked)}
              />
              <span className="font-bold text-slate-200">Treat Import as Table Cube Pool</span>
            </label>
            {isCubeInput && (
              <select
                value={cubeMode}
                onChange={(e) => setCubeMode(e.target.value as 'sealed' | 'battlebox')}
                className="px-2.5 py-1 rounded-lg bg-slate-950 border border-slate-700 text-xs"
              >
                <option value="sealed">Deal 45-Card Sealed Pools to Seats</option>
                <option value="battlebox">Split Cube as Shared Battlebox</option>
              </select>
            )}
          </div>
        </div>
      )}

      {/* Review & Inline Fuzzy Typo Fixer Step */}
      {importReview && (
        <div className="p-3 rounded-xl bg-slate-900 border border-amber-500/50 space-y-2.5">
          <div className="flex items-center justify-between">
            <span className="font-bold text-amber-300">
              Import Review ({importReview.source}) — {importReview.deck.name}
            </span>
            {!importReview.requiresPasteFallback && (
              <button
                type="button"
                onClick={async () => {
                  await commitDeckToSeat(importReview.deck, Boolean(importReview.isCube));
                  onClose();
                }}
                className="px-3 py-1 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-extrabold text-[11px]"
              >
                Load Anyway (Sandbox Allowed)
              </button>
            )}
          </div>

          {importReview.requiresPasteFallback && importReview.fallbackInstructions && (
            <div className="p-2 rounded-lg bg-indigo-950/80 border border-indigo-500/50 text-indigo-200 text-[11px]">
              <strong>Paste Fallback:</strong> {importReview.fallbackInstructions}
            </div>
          )}

          {importReview.unresolvedLines.length > 0 && (
            <div className="space-y-1.5">
              <div className="text-[10px] font-bold uppercase text-amber-300">
                Fix Unrecognized / Fuzzy Lines Inline:
              </div>
              {importReview.unresolvedLines.map((u) => (
                <div
                  key={u.lineNumber}
                  className="p-2 rounded-lg bg-slate-950 border border-slate-800 flex flex-wrap items-center justify-between gap-2 text-[11px]"
                >
                  <span>
                    Line {u.lineNumber}: <code className="text-rose-300">{u.attemptedName}</code>
                  </span>
                  <div className="flex flex-wrap gap-1">
                    {u.suggestions.map((sug) => (
                      <button
                        key={sug}
                        type="button"
                        onClick={() => {
                          const lines = rawText.split(/\r?\n/);
                          lines[u.lineNumber - 1] = `${u.parsedQuantity} ${sug}`;
                          const next = lines.join('\n');
                          setRawText(next);
                          handleUniversalImport(next);
                        }}
                        className="px-2 py-0.5 rounded bg-emerald-600/30 hover:bg-emerald-600/50 border border-emerald-400/60 text-emerald-200 font-semibold"
                      >
                        Use &ldquo;{sug}&rdquo;
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {importReview.legalityWarnings.length > 0 && (
            <div className="text-[11px] text-amber-200/90 space-y-0.5 max-h-24 overflow-y-auto">
              {importReview.legalityWarnings.slice(0, 5).map((lw, idx) => (
                <div key={idx}>• {lw}</div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Deck-Identity Sleeves & Playmat Picker (Card Art Only — No External Uploads) */}
      {mySeat && (
        <div className="space-y-2.5 pt-3 border-t border-slate-800">
          <div className="flex items-center justify-between gap-2">
            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
              Sleeves & Playmat (Deck Identity Card Art Only)
            </span>
            <div className="flex items-center gap-2">
              <span className="text-[10px] text-slate-500">No external uploads</span>
              {onOpenCosmetics && (
                <button
                  type="button"
                  onClick={() => {
                    onClose();
                    onOpenCosmetics();
                  }}
                  className="px-2.5 py-1 rounded-lg bg-amber-500/20 hover:bg-amber-500/30 border border-amber-400/50 text-amber-200 font-bold text-[10px]"
                >
                  Open Visual Art Gallery
                </button>
              )}
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
            <label className="space-y-1">
              <span className="text-[11px] font-semibold text-slate-300">Card Sleeves</span>
              <select
                value={mySeat.sleeveCardName ?? ''}
                onChange={(e) =>
                  onDispatch({
                    type: 'SET_SEAT_COSMETICS',
                    sleeveCardName: e.target.value || null,
                  })
                }
                className="w-full px-2.5 py-1.5 rounded-xl bg-slate-900 border border-slate-700 text-xs"
              >
                <option value="">Original Classic Card Back (Default)</option>
                {legalArtPool.map((opt) => (
                  <option key={`slv_${opt.cardName}`} value={opt.cardName}>
                    {opt.isCommander ? `★ ${opt.cardName} (Commander Art)` : `${opt.cardName} Art`}
                  </option>
                ))}
              </select>
            </label>

            <label className="space-y-1">
              <span className="text-[11px] font-semibold text-slate-300">Playmat Surface</span>
              <select
                value={mySeat.playmatCardName ?? ''}
                onChange={(e) =>
                  onDispatch({
                    type: 'SET_SEAT_COSMETICS',
                    playmatCardName: e.target.value || null,
                  })
                }
                className="w-full px-2.5 py-1.5 rounded-xl bg-slate-900 border border-slate-700 text-xs"
              >
                <option value="">Auto Distinct Deck-Identity Mat (Default)</option>
                {legalArtPool.map((opt) => (
                  <option key={`mat_${opt.cardName}`} value={opt.cardName}>
                    {opt.isCommander ? `★ ${opt.cardName} (Commander Art)` : `${opt.cardName} Art`}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <p className="text-[10px] text-slate-500">
            Default playmats automatically tint to your deck&apos;s color identity while staying distinct from every opponent at the table.
          </p>
        </div>
      )}
    </ModalShell>
  );
}

// ============================================================================
// 5. ACCOUNT & LINKED LOGINS MODAL (DECKSTR + TWITCH + DISCORD)
// ============================================================================

export function AuthAccountModal({
  profile,
  devAuthEnabled,
  onProfileChanged,
  onSignedOut,
  onClose,
}: {
  profile: AccountProfileDTO | null;
  devAuthEnabled: boolean;
  onProfileChanged: (next: AccountProfileDTO) => void;
  onSignedOut: () => void;
  onClose: () => void;
}) {
  const [handleInput, setHandleInput] = useState(profile?.displayName ?? 'Planeswalker');
  const [colorInput, setColorInput] = useState(profile?.profileColor ?? '#f59e0b');
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  const providers: { id: AuthProviderId; label: string; badgeClass: string }[] = [
    { id: 'deckstr', label: 'Deckstr', badgeClass: 'bg-amber-500/20 border-amber-400/60 text-amber-200' },
    { id: 'twitch', label: 'Twitch', badgeClass: 'bg-purple-500/20 border-purple-400/60 text-purple-200' },
    { id: 'discord', label: 'Discord', badgeClass: 'bg-indigo-500/20 border-indigo-400/60 text-indigo-200' },
  ];

  const handleProviderAction = async (provider: AuthProviderId, mode: 'signin' | 'link' | 'reauth') => {
    setError(null);
    setInfo(null);
    try {
      if (devAuthEnabled) {
        const res = await playtableApi.devLogin({
          provider,
          handle: handleInput.trim() || 'Planeswalker',
          mode,
        });
        onProfileChanged(res.profile);
        setInfo(
          mode === 'link'
            ? `Linked ${provider.toUpperCase()} to your Playtable account.`
            : mode === 'reauth'
            ? `Re-authenticated via ${provider.toUpperCase()}.`
            : `Signed in via ${provider.toUpperCase()}.`
        );
      } else {
        const { authorizeUrl } = await playtableApi.startOAuth(provider, mode);
        window.location.href = authorizeUrl;
      }
    } catch (err: any) {
      setError(err.message);
    }
  };

  return (
    <ModalShell
      title={profile ? `Playtable Account (${profile.publicId})` : 'Sign In to Playtable'}
      onClose={onClose}
    >
      {error && (
        <div className="p-2.5 rounded-lg bg-rose-950/90 border border-rose-500/60 text-rose-200">{error}</div>
      )}
      {info && (
        <div className="p-2.5 rounded-lg bg-emerald-950/90 border border-emerald-500/60 text-emerald-200">{info}</div>
      )}

      {!profile ? (
        <div className="space-y-3">
          <p className="text-slate-400 leading-relaxed">
            Sign in with Deckstr, Twitch, or Discord. Accounts never auto-merge by email; once signed in, you can safely link all three providers to one Playtable identity.
          </p>

          {devAuthEnabled && (
            <div>
              <label className="block text-[10px] uppercase font-bold text-slate-400 mb-1">
                Display Name (Local Dev Sign-In)
              </label>
              <input
                value={handleInput}
                onChange={(e) => setHandleInput(e.target.value)}
                className="w-full px-3 py-2 rounded-lg bg-slate-900 border border-slate-700"
              />
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            {providers.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => handleProviderAction(p.id, 'signin')}
                className={`py-2.5 px-3 rounded-xl border font-bold text-center hover:brightness-110 ${p.badgeClass}`}
              >
                Continue with {p.label}
              </button>
            ))}
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Profile Settings */}
          <div className="space-y-2">
            <div className="text-[10px] font-bold uppercase text-slate-400">Table Identity (No Email Shown)</div>
            <div className="flex gap-2 items-center">
              <input
                value={handleInput}
                onChange={(e) => setHandleInput(e.target.value)}
                className="flex-1 px-3 py-1.5 rounded-lg bg-slate-900 border border-slate-700"
              />
              <input
                type="color"
                value={colorInput}
                onChange={(e) => setColorInput(e.target.value)}
                className="w-9 h-8 rounded bg-slate-900 border border-slate-700 cursor-pointer"
              />
              <button
                type="button"
                onClick={async () => {
                  try {
                    const res = await playtableApi.updateProfile({
                      displayName: handleInput,
                      profileColor: colorInput,
                    });
                    onProfileChanged(res.profile);
                    setInfo('Profile updated.');
                  } catch (err: any) {
                    setError(err.message);
                  }
                }}
                className="px-3 py-1.5 rounded-lg bg-amber-500 text-slate-950 font-bold"
              >
                Save
              </button>
            </div>
          </div>

          {/* Linked Logins */}
          <div className="space-y-2 pt-2 border-t border-slate-800">
            <div className="text-[10px] font-bold uppercase text-slate-400">
              Linked Sign-In Providers (Deckstr · Twitch · Discord)
            </div>
            <div className="space-y-1.5">
              {providers.map((p) => {
                const linked = profile.linkedProviders.find((l) => l.provider === p.id);
                return (
                  <div
                    key={p.id}
                    className="flex items-center justify-between p-2 rounded-xl bg-slate-900 border border-slate-800"
                  >
                    <div>
                      <span className="font-bold text-slate-100">{p.label}</span>
                      {linked ? (
                        <span className="ml-2 text-emerald-300 text-[11px]">
                          Linked as {linked.providerHandle}
                        </span>
                      ) : (
                        <span className="ml-2 text-slate-500 text-[11px]">Not linked</span>
                      )}
                    </div>
                    {linked ? (
                      <div className="flex items-center gap-1.5">
                        <button
                          type="button"
                          onClick={() => handleProviderAction(p.id, 'reauth')}
                          className="px-2 py-1 rounded bg-slate-800 hover:bg-slate-700 text-[10px]"
                        >
                          Re-Auth
                        </button>
                        <button
                          type="button"
                          disabled={profile.linkedProviders.length <= 1}
                          onClick={async () => {
                            try {
                              const res = await playtableApi.unlinkProvider(p.id);
                              onProfileChanged(res.profile);
                              setInfo(`Unlinked ${p.label}.`);
                            } catch (err: any) {
                              setError(err.message);
                            }
                          }}
                          className="px-2 py-1 rounded bg-rose-950 hover:bg-rose-900 border border-rose-700/60 text-rose-200 text-[10px] disabled:opacity-40"
                        >
                          Unlink
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => handleProviderAction(p.id, 'link')}
                        className="px-2.5 py-1 rounded-lg bg-amber-500/20 border border-amber-400/60 text-amber-200 font-bold text-[10px]"
                      >
                        + Link {p.label}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Security & Privacy Controls */}
          <div className="flex flex-wrap justify-between gap-2 pt-3 border-t border-slate-800">
            <div className="flex gap-2">
              <button
                type="button"
                onClick={async () => {
                  const data = await playtableApi.exportAccount();
                  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `playtable-account-${profile.publicId}.json`;
                  a.click();
                  URL.revokeObjectURL(url);
                }}
                className="px-2.5 py-1.5 rounded-lg bg-slate-900 hover:bg-slate-800 border border-slate-700 flex items-center gap-1"
              >
                <Download className="w-3.5 h-3.5" />
                <span>Export My Data</span>
              </button>

              <button
                type="button"
                onClick={async () => {
                  await playtableApi.deleteAccount();
                  onSignedOut();
                  onClose();
                }}
                className="px-2.5 py-1.5 rounded-lg bg-rose-950/80 hover:bg-rose-900 border border-rose-700/60 text-rose-200 flex items-center gap-1"
              >
                <Trash2 className="w-3.5 h-3.5" />
                <span>Delete & Anonymize</span>
              </button>
            </div>

            <div className="flex gap-2">
              <button
                type="button"
                onClick={async () => {
                  await playtableApi.signOutEverywhere();
                  onSignedOut();
                  onClose();
                }}
                className="px-2.5 py-1.5 rounded-lg bg-slate-900 hover:bg-slate-800 border border-slate-700 text-slate-300"
              >
                Sign Out Everywhere
              </button>
              <button
                type="button"
                onClick={async () => {
                  await playtableApi.signOut();
                  onSignedOut();
                  onClose();
                }}
                className="px-2.5 py-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-white flex items-center gap-1"
              >
                <LogOut className="w-3.5 h-3.5" />
                <span>Sign Out</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </ModalShell>
  );
}

// ============================================================================
// 6. SETUP, SOUND MIXER, STREAM TOOLS & EXPORTABLE GAME LOG MODAL
// ============================================================================

export function SetupAndLogModal({
  view,
  overlaySecret,
  reducedMotion,
  onToggleReducedMotion,
  onDispatch,
  onHostAction,
  onClose,
}: {
  view: ClientGameView;
  overlaySecret: string | null;
  reducedMotion: boolean;
  onToggleReducedMotion: (next: boolean) => void;
  onDispatch: (intent: GameIntent) => void;
  onHostAction: (action: Record<string, unknown>) => void;
  onClose: () => void;
}) {
  const [tab, setTab] = useState<'setup' | 'audio' | 'log'>('setup');
  const [logFilter, setLogFilter] = useState('');
  const [soundPrefs, setSoundPrefs] = useState<SoundPreferences>(() => soundEngine.getPrefs());
  const [copiedKey, setCopiedKey] = useState<string | null>(null);

  const mySeat = view.viewerSeatId ? view.seats[view.viewerSeatId] : null;
  const filteredLog = view.recentLog.filter(
    (e) =>
      !logFilter.trim() ||
      e.summary.toLowerCase().includes(logFilter.toLowerCase()) ||
      e.actorName.toLowerCase().includes(logFilter.toLowerCase())
  );

  const copyText = (key: string, text: string) => {
    navigator.clipboard?.writeText(text);
    setCopiedKey(key);
    setTimeout(() => setCopiedKey(null), 1500);
  };

  const privateHandUrl = `${getSessionBaseUrl()}?tableId=${encodeURIComponent(view.tableId)}&session=private`;
  const spectatorUrl = `${getSessionBaseUrl()}?invite=${encodeURIComponent(view.inviteCode)}&spectate=1`;
  const obsOverlayUrl = overlaySecret
    ? `${getSessionBaseUrl()}?tableId=${encodeURIComponent(view.tableId)}&overlaySecret=${encodeURIComponent(overlaySecret)}`
    : null;

  return (
    <ModalShell title={`Table ${view.inviteCode} — Setup, Audio & Game Log`} onClose={onClose} maxWidth="max-w-2xl">
      <div className="flex gap-1.5 border-b border-slate-800 pb-2">
        {(
          [
            ['setup', 'Table & Stream Setup'],
            ['audio', 'Audio & Accessibility'],
            ['log', `Game Log (${view.recentLog.length})`],
          ] as const
        ).map(([id, label]) => (
          <button
            key={id}
            type="button"
            onClick={() => setTab(id)}
            className={`px-3 py-1.5 rounded-lg font-bold ${
              tab === id ? 'bg-amber-500 text-slate-950' : 'bg-slate-900 text-slate-300 hover:bg-slate-800'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === 'setup' && (
        <div className="space-y-4">
          {/* Invite & Stream Surface Links */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <div className="p-2.5 rounded-xl bg-slate-900 border border-slate-800 flex items-center justify-between">
              <div>
                <div className="text-[10px] uppercase text-slate-400 font-bold">Invite Code</div>
                <div className="font-mono font-extrabold text-base text-amber-300">{view.inviteCode}</div>
              </div>
              <button
                type="button"
                onClick={() => copyText('code', view.inviteCode)}
                className="px-2.5 py-1 rounded-lg bg-slate-800 hover:bg-slate-700 flex items-center gap-1"
              >
                {copiedKey === 'code' ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
                <span>Copy</span>
              </button>
            </div>

            <div className="p-2.5 rounded-xl bg-slate-900 border border-slate-800 flex items-center justify-between">
              <div>
                <div className="text-[10px] uppercase text-slate-400 font-bold">Guest Spectator Link</div>
                <div className="text-[11px] text-slate-300">Read-only, zero account needed</div>
              </div>
              <button
                type="button"
                onClick={() => copyText('spec', spectatorUrl)}
                className="px-2.5 py-1 rounded-lg bg-slate-800 hover:bg-slate-700 flex items-center gap-1"
              >
                {copiedKey === 'spec' ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
                <span>Copy Link</span>
              </button>
            </div>
          </div>

          {/* Stream Mode & Private Hand Dock */}
          {mySeat && (
            <div className="p-3 rounded-xl bg-slate-900/90 border border-slate-800 space-y-2">
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-bold text-slate-100 flex items-center gap-1.5">
                    <EyeOff className="w-3.5 h-3.5 text-rose-400" />
                    <span>Stream Mode (Anti-Stream-Snipe)</span>
                  </div>
                  <p className="text-[11px] text-slate-400">
                    Note: Web pages cannot detect screen capture automatically—Stream Mode is an explicit setting that strips your hand & private reveals from this window over the network.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => onDispatch({ type: 'TOGGLE_STREAM_MODE', enabled: !mySeat.streamMode })}
                  className={`px-3 py-1.5 rounded-lg font-extrabold shrink-0 ${
                    mySeat.streamMode ? 'bg-rose-500 text-white' : 'bg-slate-800 text-slate-200'
                  }`}
                >
                  {mySeat.streamMode ? 'Stream Mode ON' : 'Turn ON'}
                </button>
              </div>

              <div className="flex flex-wrap gap-2 pt-1">
                <a
                  href={privateHandUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="px-2.5 py-1 rounded-lg bg-indigo-600/30 border border-indigo-400/60 text-indigo-200 font-semibold inline-flex items-center gap-1"
                >
                  <span>Open Private Hand / OBS Control Dock</span>
                  <ExternalLink className="w-3 h-3" />
                </a>
                {obsOverlayUrl && (
                  <button
                    type="button"
                    onClick={() => copyText('obs', obsOverlayUrl)}
                    className="px-2.5 py-1 rounded-lg bg-slate-800 hover:bg-slate-700 border border-slate-700 text-slate-200"
                  >
                    {copiedKey === 'obs' ? 'Copied OBS URL!' : 'Copy OBS Display Overlay URL'}
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Match Lifecycle & Host Controls */}
          <div className="flex flex-wrap gap-2 items-center">
            <button
              type="button"
              onClick={() => {
                onDispatch({ type: 'START_MATCH', autoShuffle: true });
                onClose();
              }}
              className="px-3.5 py-1.5 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-extrabold"
            >
              {view.status === 'active' ? 'Restart & Deal New Match' : 'Start Match (Shuffle & Deal 7)'}
            </button>

            <button
              type="button"
              onClick={() => onDispatch({ type: 'MULLIGAN' })}
              className="px-3 py-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-100 font-semibold"
            >
              London Mulligan
            </button>

            {(view.isHost || view.isJudge) && (
              <>
                <button
                  type="button"
                  onClick={() => onDispatch({ type: 'RESET_MATCH' })}
                  className="px-3 py-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-amber-300 font-semibold"
                >
                  Reset Table
                </button>
                <button
                  type="button"
                  onClick={() => onHostAction({ kind: 'LOCK_TABLE', locked: !view.locked })}
                  className="px-3 py-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-200 flex items-center gap-1"
                >
                  {view.locked ? <Unlock className="w-3.5 h-3.5" /> : <Lock className="w-3.5 h-3.5" />}
                  <span>{view.locked ? 'Unlock Seats' : 'Lock Seats'}</span>
                </button>
              </>
            )}
          </div>

          {/* Seat Status & Host Roster */}
          {mySeat && (
            <div className="flex items-center gap-2">
              <span className="text-slate-400 font-bold">My Seat Status:</span>
              {(['ready', 'playing', 'brb', 'afk'] as const).map((st) => (
                <button
                  key={st}
                  type="button"
                  onClick={() => onDispatch({ type: 'SET_SEAT_STATUS', status: st })}
                  className={`px-2.5 py-1 rounded uppercase font-bold text-[10px] ${
                    mySeat.status === st ? 'bg-amber-500 text-slate-950' : 'bg-slate-900 border border-slate-700'
                  }`}
                >
                  {st}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === 'audio' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between p-3 rounded-xl bg-slate-900 border border-slate-800">
            <div>
              <div className="font-bold">Synthesized Tabletop Audio (WebAudio)</div>
              <div className="text-slate-400 text-[11px]">Zero external audio files — synthesized card slides, snaps, and riffles</div>
            </div>
            <button
              type="button"
              onClick={() => setSoundPrefs(soundEngine.updatePrefs({ muted: !soundPrefs.muted }))}
              className={`px-3 py-1.5 rounded-lg font-bold flex items-center gap-1.5 ${
                soundPrefs.muted ? 'bg-rose-950 text-rose-200 border border-rose-600/60' : 'bg-emerald-500 text-slate-950'
              }`}
            >
              {soundPrefs.muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
              <span>{soundPrefs.muted ? 'Muted' : 'Sound On'}</span>
            </button>
          </div>

          <div className="space-y-2">
            {(
              [
                ['drawSlide', 'Draw Card Slide'],
                ['playFlick', 'Play Card Flick'],
                ['tableThud', 'Battlefield Mat Thud'],
                ['shuffleRiffle', 'Shuffle Riffle'],
                ['tapClick', 'Tap / Untap Turn'],
                ['hoverTick', 'Hand Fan Hover Tick'],
                ['flagChime', 'Wait Flag Alert Chime'],
              ] as [SoundEventId, string][]
            ).map(([evId, label]) => (
              <div key={evId} className="flex items-center justify-between gap-3">
                <span className="w-44 text-slate-300">{label}</span>
                <input
                  type="range"
                  min={0}
                  max={1}
                  step={0.05}
                  value={soundPrefs.volumes[evId]}
                  onChange={(e) =>
                    setSoundPrefs(
                      soundEngine.updatePrefs({
                        eventVolumes: { [evId]: Number.parseFloat(e.target.value) },
                      })
                    )
                  }
                  className="flex-1 accent-amber-500"
                />
                <button
                  type="button"
                  onClick={() => soundEngine.play(evId)}
                  className="px-2 py-0.5 rounded bg-slate-800 hover:bg-slate-700 text-[10px]"
                >
                  Test
                </button>
              </div>
            ))}
          </div>

          <div className="pt-2 border-t border-slate-800 flex items-center justify-between">
            <div>
              <div className="font-bold">Reduced Motion</div>
              <div className="text-slate-400 text-[11px]">Disables flying card ghosts, pulsing rings, and hand drift</div>
            </div>
            <input
              type="checkbox"
              checked={reducedMotion}
              onChange={(e) => onToggleReducedMotion(e.target.checked)}
              className="w-4 h-4 accent-amber-500"
            />
          </div>
        </div>
      )}

      {tab === 'log' && (
        <div className="space-y-2">
          <div className="flex gap-2">
            <input
              value={logFilter}
              onChange={(e) => setLogFilter(e.target.value)}
              placeholder="Filter log by player or action..."
              className="flex-1 px-3 py-1.5 rounded-lg bg-slate-900 border border-slate-700"
            />
            <button
              type="button"
              onClick={() => {
                const lines = filteredLog.map((e) => `[T${e.turnNumber} #${e.seq}] ${e.summary}`).join('\n');
                const blob = new Blob([lines], { type: 'text/plain;charset=utf-8' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `playtable-${view.inviteCode}-log.txt`;
                a.click();
                URL.revokeObjectURL(url);
              }}
              className="px-3 py-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 border border-slate-700 flex items-center gap-1"
            >
              <Download className="w-3.5 h-3.5" />
              <span>Export .txt</span>
            </button>
          </div>

          <div className="max-h-72 overflow-y-auto rounded-xl bg-slate-900/90 border border-slate-800 divide-y divide-slate-800/70">
            {filteredLog
              .slice()
              .reverse()
              .map((entry) => (
                <div key={entry.seq} className="px-3 py-1.5 flex items-center justify-between gap-2">
                  <div>
                    <span className="text-amber-400/90 font-mono mr-2">[T{entry.turnNumber}]</span>
                    <span>{entry.summary}</span>
                  </div>
                  {(view.isHost || view.isJudge) && (
                    <button
                      type="button"
                      onClick={() => onDispatch({ type: 'ROLLBACK_TO_SEQ', targetSeq: entry.seq })}
                      className="px-2 py-0.5 rounded bg-slate-800 hover:bg-amber-500 hover:text-slate-950 text-[10px] shrink-0"
                    >
                      Rewind Here
                    </button>
                  )}
                </div>
              ))}
          </div>
        </div>
      )}
    </ModalShell>
  );
}

// ============================================================================
// 7. OPENING HAND / LONDON MULLIGAN MODAL
// ============================================================================

export function OpeningHandModal({
  handCards,
  libraryCount,
  mulliganCount,
  freeFirstMulligan,
  onDispatch,
  onInspect,
  onClose,
}: {
  handCards: PublicCardView[];
  libraryCount: number;
  mulliganCount: number;
  freeFirstMulligan: boolean;
  onDispatch: (intent: GameIntent) => void;
  onInspect: (card: PublicCardView) => void;
  onClose: () => void;
}) {
  const [sortByCmc, setSortByCmc] = useState(false);
  const [bottomIds, setBottomIds] = useState<string[]>([]);

  const requiredBottomCount = Math.max(0, mulliganCount - (freeFirstMulligan ? 1 : 0));
  const displayed = sortByCmc
    ? handCards.slice().sort((a, b) => (a.card?.cmc ?? 0) - (b.card?.cmc ?? 0))
    : handCards;

  const toggleBottom = (id: string) => {
    if (requiredBottomCount === 0) return;
    setBottomIds((prev) =>
      prev.includes(id)
        ? prev.filter((x) => x !== id)
        : prev.length < requiredBottomCount
        ? [...prev, id]
        : prev
    );
  };

  const keepCount = Math.max(0, handCards.length - requiredBottomCount);

  return (
    <ModalShell
      title={`Opening hand · ${mulliganCount} mulligans · ${libraryCount} library · ${handCards.length} hand`}
      onClose={onClose}
      maxWidth="max-w-4xl"
    >
      <div className="text-[11px] text-slate-400">
        Only you can see these cards.{' '}
        {freeFirstMulligan
          ? 'First mulligan is free. Later ones put cards on the bottom.'
          : 'London mulligan: select cards to put on the bottom when keeping.'}
      </div>

      {requiredBottomCount > 0 && (
        <div className="px-3 py-1.5 rounded-xl bg-amber-950/60 border border-amber-500/50 text-amber-200 font-semibold">
          Select {requiredBottomCount} card(s) to put on the bottom of your library ({bottomIds.length}/{requiredBottomCount} selected).
        </div>
      )}

      <div className="flex flex-wrap justify-center gap-2.5 py-2">
        {displayed.map((c) => {
          const markedBottom = bottomIds.includes(c.instanceId);
          return (
            <div key={c.instanceId} className="flex flex-col items-center gap-1.5">
              <div
                onClick={() => (requiredBottomCount > 0 ? toggleBottom(c.instanceId) : onInspect(c))}
                className={`rounded-xl p-1 cursor-pointer transition-transform ${
                  markedBottom
                    ? 'ring-2 ring-rose-500 opacity-55 scale-95'
                    : 'hover:-translate-y-1'
                }`}
              >
                <CardVisual item={c} size="md" />
              </div>
              <button
                type="button"
                onClick={() => onInspect(c)}
                className="text-[10px] text-slate-400 hover:text-white"
              >
                {markedBottom ? 'Bottoming' : 'View card'}
              </button>
            </div>
          );
        })}
      </div>

      <div className="flex flex-col items-center gap-3 pt-2 border-t border-slate-800">
        <button
          type="button"
          onClick={() => setSortByCmc((v) => !v)}
          className="text-xs font-semibold text-slate-300 hover:text-white"
        >
          ↑↓ {sortByCmc ? 'Unsort hand' : 'Sort by CMC'}
        </button>

        <div className="flex items-center gap-3">
          <button
            type="button"
            disabled={bottomIds.length !== requiredBottomCount}
            onClick={() => {
              onDispatch({ type: 'CONFIRM_OPENING_HAND', bottomCardIds: bottomIds });
              onClose();
            }}
            className="px-6 py-2 rounded-xl bg-emerald-500 hover:bg-emerald-400 disabled:opacity-40 text-slate-950 font-extrabold text-xs"
          >
            Keep {keepCount}
          </button>
          <button
            type="button"
            onClick={() => {
              setBottomIds([]);
              onDispatch({ type: 'MULLIGAN' });
            }}
            className="px-6 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 border border-slate-700 text-slate-100 font-bold text-xs"
          >
            Mulligan
          </button>
        </div>
      </div>
    </ModalShell>
  );
}

// ============================================================================
// 8. FLOATING "YOUR TURN" PROMPT (UNTAP [U] + DRAW [D] + SHOW EVERY TURN)
// ============================================================================

export function YourTurnPrompt({
  showEveryTurn,
  onToggleShowEveryTurn,
  onDispatch,
  onClose,
}: {
  showEveryTurn: boolean;
  onToggleShowEveryTurn: (v: boolean) => void;
  onDispatch: (intent: GameIntent) => void;
  onClose: () => void;
}) {
  const [untappedClicked, setUntappedClicked] = useState(false);

  return (
    <div className="fixed inset-x-0 top-1/3 z-50 flex justify-center pointer-events-none px-3">
      <div className="pointer-events-auto w-full max-w-sm rounded-2xl bg-slate-950/95 border border-slate-700 shadow-2xl p-4 space-y-3 text-xs">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-extrabold text-white">Your turn</h3>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => onDispatch({ type: 'UNDO_LAST_ACTION' })}
              className="px-2 py-0.5 rounded bg-slate-900 hover:bg-slate-800 border border-slate-800 text-[10px] text-slate-300"
            >
              ↺ Undo Ctrl/⌘ Z
            </button>
            <button
              type="button"
              onClick={onClose}
              className="text-slate-400 hover:text-white"
              aria-label="Close turn prompt"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2.5">
          <button
            type="button"
            onClick={() => {
              onDispatch({ type: 'UNTAP_ALL' });
              setUntappedClicked(true);
            }}
            className="py-2.5 px-3 rounded-xl bg-slate-900 hover:bg-slate-800 border border-slate-700 font-bold text-slate-100 flex items-center justify-center gap-2"
          >
            {untappedClicked && <Check className="w-3.5 h-3.5 text-emerald-400" />}
            <span>{untappedClicked ? 'Untapped' : 'Untap All'}</span>
            <kbd className="px-1.5 py-0.5 rounded bg-slate-800 text-[10px] text-slate-400">U</kbd>
          </button>

          <button
            type="button"
            onClick={() => {
              onDispatch({ type: 'DRAW_CARDS', count: 1 });
              onDispatch({ type: 'SET_PHASE', phase: 'main1' });
              onClose();
            }}
            className="py-2.5 px-3 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-extrabold flex items-center justify-center gap-2"
          >
            <span>↓ Draw</span>
            <kbd className="px-1.5 py-0.5 rounded bg-emerald-600/50 text-[10px] text-slate-950">D</kbd>
          </button>
        </div>

        <div className="flex items-center justify-between pt-2 border-t border-slate-800/80 text-[11px] text-slate-400">
          <label className="flex items-center gap-1.5 cursor-pointer">
            <input
              type="checkbox"
              checked={showEveryTurn}
              onChange={(e) => onToggleShowEveryTurn(e.target.checked)}
              className="accent-emerald-500"
            />
            <span>Show every turn</span>
          </label>
          <span>
            <kbd className="px-1 py-0.5 rounded bg-slate-900 border border-slate-800 text-[10px]">E</kbd> Show / hide
          </span>
        </div>
      </div>
    </div>
  );
}
